<template>
  <div ref="element" class="relative max-w-full overflow-auto">
    <!-- IMAGE -->
    <div ref="imageContainer" class="relative w-full bg-grey-100 p-3">
      <div class="relative">
        <AppImage
          v-if="data.image"
          class="w-full object-contain mix-blend-multiply"
          :src="data.image"
          :alt="useMapAltText(data.image)"
        />

        <!-- BADGE -->
        <div
          v-for="(tooltip, index) in data.tooltips"
          :id="`badge-${tooltip.id}`"
          :key="tooltip.id"
          :style="{
            left: `${tooltip.position_x}%`,
            top: `${tooltip.position_y}%`,
          }"
          class="absolute -translate-x-1/2 -translate-y-1/2 scroll-mt-[25vh]"
        >
          <button
            type="button"
            class="
              group flex size-6 items-center justify-center rounded-full bg-blue-500 text-white typo-body-md
            "
            :aria-describedby="`tooltip-${index}`"
            :aria-expanded="sliderContainer?.activeIndex?.value === index && isOpen"
            @click="show(tooltip.id)"
          >
            <span
              aria-hidden="true"
              class="absolute inset-0 rounded-full bg-blue-500 opacity-75"
              :class="[
                { 'animate-[ping_1.75s_ease-in-out_0.5s_infinite]': sliderContainer?.activeIndex?.value === index },
                { 'animate-[ping_1.75s_ease-in-out_0.5s_infinite]': index === 0 && !isOpen },
              ]"
            />
            <span class="relative">{{ index + 1 }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- FIXED TOOLTIP SLIDER -->
    <div
      ref="tooltipSlider"
      class="fixed inset-x-0 bottom-0 z-40 h-0 overflow-hidden rounded-tr-[3.5rem] bg-blue-500 text-white"
    >
      <div ref="tooltipSliderContent" class="py-9 opacity-0">
        <!-- Close Button -->
        <button
          :aria-label="$t('aria_labels.tooltip_close')"
          class="slide-up absolute right-5 top-5 transition-colors hover:text-blue-200"
          type="button"
          @click="hide()"
        >
          <IconPlus class="w-7 rotate-45 transition-transform group-hover:rotate-[135deg]" />
        </button>

        <ul
          ref="sliderContainer"
          v-drag-scroll
          class="flex select-none gap-5"
        >
          <li
            v-for="(tooltip, index) in data.tooltips"
            :id="`tooltip-${index}`"
            :key="tooltip.id"
            class="w-screen shrink-0 grow-0 overflow-y-auto px-5"
          >
            <!-- Title -->
            <h3 class="mb-3 flex w-full shrink-0 gap-3 pr-8">
              <span
                class="
                  flex size-7 shrink-0 items-center justify-center rounded-full bg-white !font-semibold
                  text-blue-500
                "
              >
                {{ index + 1 }}
              </span>

              <span class="!font-semibold typo-body-2xl" v-html="tooltip.heading" />
            </h3>

            <!-- Text -->
            <div class="max-h-[15vh] overflow-y-auto">
              <div class="link w-full typo-body-lg 3xl:!max-w-[60ch]" v-html="tooltip.text" />
            </div>
          </li>
        </ul>
        <!-- PAGINATION -->
        <PaginationSlider
          class="px-5"
          :amount-pages="sliderContainer?.amountPages?.value || 1"
          :active-index="sliderContainer?.activeIndex?.value || 0"
          @scroll-to-item="sliderContainer?.scrollToItem ? sliderContainer?.scrollToItem($event) : null"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import gsap from 'gsap'

import ScrollTrigger from 'gsap/ScrollTrigger'
import type { DragElement } from '~/plugins/dragScroll'
import type { ComponentsMainInteractiveDocumentFragment } from '#gql'

const props = defineProps<{
  data: Enforce<ComponentsMainInteractiveDocumentFragment>
}>()
const element = ref<HTMLElement | null>(null)
const imageContainer = ref<HTMLElement | null>(null)
const sliderContainer = ref<DragElement | null>(null)
const tooltipSlider = ref<HTMLElement | null>(null)
const tooltipSliderContent = ref<HTMLElement | null>(null)
const isOpen = ref<boolean>(false)

watch(
  () => sliderContainer.value?.activeIndex?.value,
  (newIndex) => {
    if (!isOpen.value) {
      // console.warn('Slider is not open, skipping scrollIntoView.')
      return
    }
    // Find the tooltip with the active index
    const activeTooltip = props.data.tooltips[newIndex || 0]
    debouncedScrollIntoView(activeTooltip.id)
  },
)

let tl: gsap.core.Timeline
async function show(id: string) {
  // Scroll to the tooltip in the slider
  const index = props.data.tooltips?.findIndex(tooltip => tooltip.id === id)
  if (index !== -1 && sliderContainer.value?.scrollToItem) {
    sliderContainer.value.scrollToItem(index)
  }

  if (isOpen.value) {
    return
  }

  await nextTick()

  isOpen.value = true

  if (tl?.isActive()) return // Skip if timeline is active

  // Create or reset the timeline
  tl = gsap.timeline({
    defaults: {
      duration: 0.5,
      ease: 'power1.inOut',
    },
  })

  tl
    .to(imageContainer.value, {
      width: '200%',
      onComplete: () => {
        scrollIntoView(id)
      },
    })
    .to(tooltipSlider.value, {
      autoAlpha: 1,
      duration: 0.3,
      height: 'auto',
    }, '<')
    .to(tooltipSliderContent.value, {
      autoAlpha: 1,
      duration: 0.2,
    }, '-=0.1')

  await tl
}

async function hide() {
  if (!tl) {
    console.warn('Timeline is not initialized.')
    return
  }

  // Wait for the timeline to finish if it's active
  if (tl.isActive()) {
    await new Promise((resolve) => {
      const checkActive = () => {
        if (!tl.isActive()) {
          resolve()
        } else {
          setTimeout(checkActive, 50) // Check every 50ms
        }
      }
      checkActive()
    })
  }

  // Reverse the timeline
  await tl.reverse()

  isOpen.value = false
}

function scrollIntoView(id: string) {
  const badge = document.getElementById(`badge-${id}`)
  if (badge) {
    badge.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      inline: 'center',
    })
  }
}

const debouncedScrollIntoView = useDebounceFn((id: string) => {
  scrollIntoView(id)
}, 200)

let scrollTriggerInstance: ScrollTrigger | null = null
onMounted(() => {
  scrollTriggerInstance = ScrollTrigger.create({
    trigger: element.value,
    start: '-50% top', // When center of the trigger hits the bottom of the viewport
    end: '200% top', // When center of the trigger hits the top of the viewport
    once: false,
    onLeave: () => {
      if (isOpen.value) {
        hide()
      }
    },
    onLeaveBack: () => {
      if (isOpen.value) {
        hide()
      }
    },
  })
})

onUnmounted(() => {
  tl?.kill() // Kill the timeline

  scrollTriggerInstance?.kill()
})
</script>
